import seedrandom from 'seedrandom';
import p5 from 'p5';
import { NaturalSpawnEntry, WorldDescriptor } from './types';

export function randomRNG(rng: seedrandom.PRNG, min: number, max: number) {
    return (rng() * (max - min)) + min;
}

export function randomIntRNG(rng: seedrandom.PRNG, min: number, max: number) {
    return Math.floor(randomRNG(rng, min, max));
}

//helper function to get random object from an array
export function randomArrayElementRNG(rng: seedrandom.PRNG, array: any[]) {
    return array[randomIntRNG(rng, 0, array.length)];
}

export function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}

// Normalizes a naturalObjects/naturalEntities list from world.json into
// NaturalSpawnEntry objects. Supports both formats:
//   New:    [{"name": "grass", "chance": 0.8}, ...]
//   Legacy: ["grass", "grass", "tree1", ...] (duplicates raised the odds)
// For legacy lists the chance is derived from how often a name repeats,
// so old worlds keep roughly the same object density as before.
export function normalizeSpawnEntries(list: any): NaturalSpawnEntry[] {
    if(!Array.isArray(list) || list.length === 0){
        return [];
    }

    let entries: NaturalSpawnEntry[] = [];

    //legacy string entries: chance = share of the list
    let legacyNames = list.filter(e => typeof e === 'string') as string[];
    if(legacyNames.length > 0){
        let counts: {[name: string]: number} = {};
        for(let name of legacyNames){
            counts[name] = (counts[name] || 0) + 1;
        }
        for(let name in counts){
            entries.push({name: name, chance: Math.round(counts[name] / legacyNames.length * 100) / 100});
        }
    }

    //new format entries
    for(let e of list){
        if(e && typeof e === 'object' && typeof e.name === 'string'){
            let chance = typeof e.chance === 'number' ? clamp01(e.chance) : 0.5;
            entries.push({name: e.name, chance: chance});
        }
    }

    return entries;
}

// Normalizes a raw world.json object (or null) into a WorldDescriptor.
export function normalizeWorldDescriptor(data: any): WorldDescriptor {
    if(!data || typeof data !== 'object'){
        return {
            defaultTile: "empty",
            liquid: "empty",
            naturalObjects: [],
            naturalEntities: []
        };
    }

    return {
        defaultTile: typeof data.defaultTile === 'string' ? data.defaultTile : "empty",
        liquid: typeof data.liquid === 'string' ? data.liquid : "empty",
        naturalObjects: normalizeSpawnEntries(data.naturalObjects),
        naturalEntities: normalizeSpawnEntries(data.naturalEntities)
    };
}

export function sliceImageToArray(image, frameWidth, frameHeight) {
    let frames: p5.Image[] = [];
    for (let y = 0; y < image.height; y += frameHeight) {
        for (let x = 0; x < image.width; x += frameWidth) {
            let frame = image.get(x, y, frameWidth, frameHeight);
            frames.push(frame);
        }
    }
    return frames;
}

export function setCookie(name: string, value: string, days: number) {
    let expires = "";
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + encodeURIComponent(value) + expires + "; path=/";
}


export function getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
    return null;
}

export function angleToDirection(angle:number, directions:number = 8):number{
    let halfStep = p5js.TWO_PI / directions / 2;
    // Parenthesized so the whole index wraps into [0, directions) — previously
    // `% directions` only applied to the literal `2` due to operator precedence,
    // which could let this return out-of-range direction indices.
    return (p5js.int(p5js.map(angle, p5js.PI, -p5js.PI, directions, 0) + halfStep) + 2) % directions;
}

export function directionToAngle(direction:number, directions:number = 8):number{
    return p5js.map(direction, 0, directions, -p5js.PI, p5js.PI) - p5js.HALF_PI;
}

// Deterministic per-entity identity, used to persist which chunk-defined
// entities the player has killed/collected/opened. The seed folds in the world
// name and spawn position so the same entity always maps to the same hash
// across chunk reloads and world changes (see InteractionState).
export function generateEntityHash(world: string, kind: string, x: number, y: number): string {
    let seed = `${world}|${kind}|${x}|${y}`;
    // djb2 string hash, emitted as an unsigned 32-bit hex string
    let hash = 5381;
    for(let i = 0; i < seed.length; i++){
        hash = (((hash << 5) + hash) ^ seed.charCodeAt(i)) >>> 0;
    }
    return `${kind}_${hash.toString(16)}`;
}

export function saveJSON(json: any, path: string){
    //use POST request to upload the JSON to the server
    let xhr = new XMLHttpRequest();
    xhr.open("POST", `${staticServer}${path}`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.send(JSON.stringify(json));
}

export function mouseInArea(x: number, y: number, w: number, h: number): boolean{
    let mx = p5js.mouseX/pixelSize - buffer.width/2;
    let my = p5js.mouseY/pixelSize - buffer.height/2;

    if(mx > x && mx < x + w && my > y && my < y + h){
        return true;
    }
    return false;
}

export function drawLight(posX: number, posY: number, size: number, c: p5.Color = p5js.color(255, 150, 50)){
    bufferTop.tint(c);
    bufferTop.image(globalThis.lightImage, posX - size/2, posY - (size*0.7)/2, size, size*0.7);
    bufferTop.noTint();
}